import React, {PureComponent} from 'react';
import {withFlags} from 'flag';
import fx from 'glfx';
import bytes from 'bytes';
// import Drift from 'react-driftjs';

import {ImageCrop} from 'modules';
import {AdjustmentMenu, BannerAd, Button, ImageCompressor, ImageEditor, ImageFormatChanger, ImageResizer, Loader} from 'components';
import withPageTracking from 'hoc/withPageTracking';

const MAX_WIDTH = 1000;
const THUMBNAIL_SIZE = 100;

class ImageAdjust extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      cropper: null,
      filterCanvas: null,
      thumbnailImage: null,
      filterImage: null,
      cropWindow: this.getDefaultCropWindow(),
      fileSize: '',
      fileSizeSuffix: 'kb',
      quality: '',
      format: props.originalImage.type,
      resizeWidth: 0,
      resizeHeight: 0,
      resizePPI: '',
      filter: null
    };
  }

  getDefaultCropWindow() {
    const {originalImage} = this.props;

    return {
      x: 0,
      y: 0,
      width: originalImage.dimensions.width,
      height: originalImage.dimensions.height
    };
  }

  onCropApplied = (cropWindow = this.getDefaultCropWindow()) => this.setState({
    cropWindow: {
      x: this.state.cropWindow.x + cropWindow.x,
      y: this.state.cropWindow.y + cropWindow.y,
      width: cropWindow.width,
      height: cropWindow.height
    }
  });

  onCropReset = () => this.setState({
    cropWindow: this.getDefaultCropWindow()
  });

  initCropper = (editor) => {

    if (!editor) {
      return;
    }

    this.state.cropper.init({
      base: editor.baseCanvas,
      selection: editor.selectionCanvas
    });
    this.state.cropper.disable('selection');
  };

  handleCompressOptionChange = (options) => {
    if(options.quality > 0) {
      options.fileSize = '';
    }
    this.setState({
      ...options
    })
  };

  handleResizeOptionChange = ({ width, height, ppi, suffix }) => this.setState({
    resizeWidth: width,
    resizeHeight: height,
    resizePPI: ppi,
    resizeSuffix: suffix
  });

  handleFilterDrawn = (filter) => this.setState({ filter });

  resizeImage = () => {
    const {originalImage, resizeImage} = this.props;
    const {
      cropper,
      cropWindow,
      format,
      quality,
      fileSize,
      fileSizeSuffix,
      resizeWidth,
      resizeHeight,
      filter,
      resizePPI,
      resizeSuffix
    } = this.state;

    const body = {
      imageId: originalImage.imageId,
      crop: {
        x: parseInt(cropWindow.x, 10),
        y: parseInt(cropWindow.y, 10),
        width: parseInt(cropWindow.width, 10),
        height: parseInt(cropWindow.height, 10)
      },
      flip: cropper.flopped, // the client and services use different names, we patch over it here
      flop: cropper.flipped, // the client and services use different names, we patch over it here
      rotate: cropper.rotation
    };

    if (format) {
      body.imageType = format;
    }

    if (resizeWidth || resizeHeight) {
      body.resize = {
        width: Math.round(resizeWidth),
        height: Math.round(resizeHeight)
      };
    }
    if(resizePPI) {
      body.ppi = resizePPI;
      body.suffix = resizeSuffix;
    }

    if (filter && Object.keys(filter).length) {
      body.filter = filter;
    }

    if (quality || fileSize) {
      body.compress = {};

      if (quality) {
        body.compress.quality = parseInt(quality, 10);
      }

      if (fileSize) {
        body.compress.maxFileSize = Math.round(bytes(`${parseInt(fileSize, 10)}${fileSizeSuffix}`) / 1024); // The API wants kilobytes
      }
    }

    resizeImage([body]);
  };

  addEventHandlers(cropper) {
    cropper.on('cropApplied', this.onCropApplied);
    cropper.on('cropReset', this.onCropReset);
  }

  removeEventHandlers(cropper) {
    cropper.off('cropApplied', this.onCropApplied);
    cropper.off('cropReset', this.onCropReset);
  }

  generateThumbnailImage() {
    const {image} = this.props;
    const thumbnailImage = document.createElement('canvas');

    // Set the size of the thumbnail canvas
    if (image.width > image.height) {
      thumbnailImage.height = image.height * THUMBNAIL_SIZE / image.width;
      thumbnailImage.width = THUMBNAIL_SIZE;
    } else {
      thumbnailImage.width = image.width * THUMBNAIL_SIZE / image.height;
      thumbnailImage.height = THUMBNAIL_SIZE;
    }

    thumbnailImage
      .getContext('2d')
      .drawImage(image, 0, 0, thumbnailImage.width, thumbnailImage.height);

    this.setState({ thumbnailImage });
  }

  generateFilterImage() {
    const {image} = this.props;
    const {x, y, width, height} = this.state.cropWindow;
    const scaleFactor = Math.min(MAX_WIDTH / width, 1);
    const filterImage = document.createElement('canvas');

    // Set the size of the filter canvas
    filterImage.width = width * scaleFactor;
    filterImage.height = height * scaleFactor;

    filterImage
      .getContext('2d')
      .drawImage(
        image,
        x,
        y,
        width,
        height,
        0,
        0,
        filterImage.width,
        filterImage.height
      );

    this.setState({ filterImage });
  }

  componentDidUpdate(prevProps, prevState) {
    const {cropWindow} = this.state;

    // The image has been updated, so we will create new reduced size versions
    if (prevState.cropWindow !== cropWindow) {
      this.generateFilterImage();
    }
  }

  componentDidMount() {
    const {image, flags} = this.props;
    let {filterCanvas} = this.state;

    // Generate smaller images based on the original image
    this.generateThumbnailImage();
    this.generateFilterImage();

    // Create a glfx canvas off-screen. When we need to apply
    // a filter, we will do that, then call this.cropper.draw()
    // to take in the changes on it.
    if (flags.userAgent.webgl) {
      filterCanvas = fx.canvas();
    }

    const fourthCanvas = document.createElement('canvas');
    fourthCanvas.width = image.width;
    fourthCanvas.height = image.height;
    fourthCanvas.getContext('2d').drawImage(image, 0, 0);

    // Initialize image cropper with the original image
    const cropper = new ImageCrop(fourthCanvas);

    this.addEventHandlers(cropper);

    this.setState({
      cropper,
      filterCanvas
    });
  }

  componentWillUnmount() {
    window.URL.revokeObjectURL(this.props.image.preview);

    // Theoretically, we can unmount before the image loads so
    // we should protect against that case.
    if (this.state.cropper) {
      this.removeEventHandlers(this.state.cropper);
      this.state.cropper.destroy();
    }
  }

  renderEditor() {
    const {image} = this.props;
    const {cropper, cropWindow, filterCanvas, thumbnailImage, filterImage} = this.state;

    return (
      <div style={{
        margin: '0 auto',
        maxWidth: `${MAX_WIDTH}px`
      }}>
        <AdjustmentMenu
          image={image}
          cropper={cropper}
          filterCanvas={filterCanvas}
          thumbnailImage={thumbnailImage}
          filterImage={filterImage}
          imageWidth={cropWindow.width}
          imageHeight={cropWindow.height}
          maxWidth={MAX_WIDTH}
          onFilterDrawn={this.handleFilterDrawn}
        />
        <ImageEditor
          ref={this.initCropper}
          image={image}
          maxWidth={MAX_WIDTH}
        />
      </div>
    );
  }

  render() {
    const {originalImage} = this.props;
    const {cropper, fileSize, fileSizeSuffix, quality, format} = this.state;

    if (!cropper) {
      return <Loader />;
    }

    return (
      <div className='container'>
        {/* <Drift appId="rzyr4ptivr3s" /> */}
        <div className='section'>
          <h2><span className='step'>1</span> Adjust Image (Optional)</h2>
          {this.renderEditor()}
        </div>
        <div className='section'>
          <h2><span className='step'>2</span> Resize your Image</h2>
          <ImageResizer
            originalImage={originalImage}
            cropper={cropper}
            onOptionChange={this.handleResizeOptionChange}
          />
        </div>
        <div className='section'>
          <h2><span className='step'>3</span> Optimize Your Image (optional)</h2>
          <ImageCompressor
            showFileSizeInput="yes"
            fileSize={fileSize}
            fileSizeSuffix={fileSizeSuffix}
            quality={quality}
            format={format}
            onOptionChange={this.handleCompressOptionChange}
          />
        </div>
        <div className='section'>
          <h2><span className='step'>4</span> Save Image As</h2>
          <ImageFormatChanger
            format={format}
            onOptionChange={this.handleCompressOptionChange}
          />
        </div>
        <BannerAd />
        <div className='section'>
          <Button
            className='main-button'
            onClick={this.resizeImage}
          >
            Resize Image
          </Button>
        </div>
      </div>
    );
  }
}

export default withPageTracking(withFlags(ImageAdjust), '/single-editor.html');



// WEBPACK FOOTER //
// ./src/containers/ImageResize/ImageAdjust.jsx